/**
 * Shared bulk component (gap 1 PR 2, D-18 item 7). OPEN-213 reuses it; keep it free of domain
 * logic.
 *
 * One amount field with a $/% toggle. readAmountChange turns the draft into a PriceChange:
 * dollars become whole cents (parseDollarsToCents), a percentage becomes basis points
 * (parsePercentToBasisPoints). Either may be negative (a cut). No float math anywhere.
 */

import React from 'react';
import {
  hasSubCentPrecision,
  parseDollarsToCents,
  parsePercentToBasisPoints,
  type PriceChange,
} from '@lodex-pro/shared/money';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';

export interface AmountDraft {
  readonly mode: 'DOLLARS' | 'PERCENT';
  readonly raw: string;
}

/** The API's dollar limit, in cents ($10,000.00 either way). */
const MAX_DOLLAR_CHANGE_CENTS = 1_000_000;
const DOLLARS_HINT = 'Use dollars and cents, e.g. 1.25.';

export function readAmountChange(
  mode: 'DOLLARS' | 'PERCENT',
  raw: string,
): { ok: true; change: PriceChange } | { ok: false; error: string } {
  const text = raw.trim();
  if (text === '') return { ok: false, error: 'Enter an amount.' };
  if (mode === 'PERCENT') {
    const r = parsePercentToBasisPoints(text);
    return r.ok
      ? { ok: true, change: { mode: 'PERCENT', basisPoints: r.bp } }
      : { ok: false, error: r.error };
  }
  // One leading sign, then plain dollars ("$" and thousands separators allowed).
  const negative = text.startsWith('-');
  const rest = text.startsWith('-') || text.startsWith('+') ? text.slice(1).trim() : text;
  if (rest === '' || rest.startsWith('-') || rest.startsWith('+') || hasSubCentPrecision(rest)) {
    return { ok: false, error: DOLLARS_HINT };
  }
  const r = parseDollarsToCents(rest);
  if (!r.ok) return { ok: false, error: DOLLARS_HINT };
  if (r.cents > MAX_DOLLAR_CHANGE_CENTS) {
    return { ok: false, error: 'The largest change is $10,000.00.' };
  }
  const cents = negative && r.cents !== 0 ? -r.cents : r.cents;
  return { ok: true, change: { mode: 'DOLLARS', cents } };
}

const errorStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'var(--color-danger)',
  marginTop: '4px',
};

const MODES: readonly { readonly mode: AmountDraft['mode']; readonly label: string }[] = [
  { mode: 'DOLLARS', label: '$' },
  { mode: 'PERCENT', label: '%' },
];

export function AmountChangeInput(p: {
  value: AmountDraft;
  onChange(v: AmountDraft): void;
  error?: string | null;
  id?: string;
}): React.JSX.Element {
  const fallbackId = React.useId();
  const id = p.id ?? fallbackId;
  const error = p.error ?? null;
  return (
    <div>
      <label
        htmlFor={id}
        style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '4px' }}
      >
        Amount
      </label>
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
        <div role="group" aria-label="Change by" style={{ display: 'flex' }}>
          {MODES.map((m) => (
            <Button
              key={m.mode}
              type="button"
              size="sm"
              variant={p.value.mode === m.mode ? 'default' : 'outline'}
              aria-pressed={p.value.mode === m.mode}
              onClick={(): void => p.onChange({ mode: m.mode, raw: p.value.raw })}
            >
              {m.label}
            </Button>
          ))}
        </div>
        <Input
          id={id}
          inputMode="decimal"
          value={p.value.raw}
          aria-invalid={error !== null}
          placeholder={p.value.mode === 'DOLLARS' ? '0.25' : '5'}
          onChange={(e): void => p.onChange({ mode: p.value.mode, raw: e.target.value })}
        />
      </div>
      {error !== null && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
    </div>
  );
}
